import type * as ents from '@attain-apps/entities'

/** State of configurable Space dimension filter features. */
export interface State {
  readonly optionsLookup: Readonly<
    Partial<Record<ents.core.dms.models.Entity.Id, State.Options>>
  >
}

export namespace State {
  const FALLBACK_ERROR_MESSAGE = 'Unable to load options.'

  export type Status = 'idle' | 'loading' | 'loaded' | 'error'

  /** Loading state and selectable values for one DMS entity. */
  export interface Options {
    readonly errorMessage?: string
    readonly status: Status
    readonly values: readonly ents.extra.space.Filter.Option[]
  }

  /** Returned by selectors until an entity has started loading. */
  export const IDLE_OPTIONS: Options = {
    errorMessage: undefined,
    status: 'idle',
    values: [],
  }

  export const INITIAL: State = {
    optionsLookup: {},
  }

  function updateOptions(
    state: State,
    entityId: ents.core.dms.models.Entity.Id,
    update: (current: Options) => Options,
  ): State {
    const current = state.optionsLookup[entityId] ?? IDLE_OPTIONS

    return {
      ...state,
      optionsLookup: {
        ...state.optionsLookup,
        [entityId]: update(current),
      },
    }
  }

  export function setOptionsLoading(
    state: State,
    entityId: ents.core.dms.models.Entity.Id,
  ): State {
    return updateOptions(state, entityId, current => ({
      ...current,
      errorMessage: undefined,
      status: 'loading',
    }))
  }

  export function setOptionsLoaded(
    state: State,
    entityId: ents.core.dms.models.Entity.Id,
    values: readonly ents.extra.space.Filter.Option[],
  ): State {
    return updateOptions(state, entityId, current => ({
      ...current,
      errorMessage: undefined,
      status: 'loaded',
      values,
    }))
  }

  export function setOptionsError(
    state: State,
    entityId: ents.core.dms.models.Entity.Id,
    error: Error,
  ): State {
    return updateOptions(state, entityId, current => ({
      ...current,
      errorMessage: error.message.trim() || FALLBACK_ERROR_MESSAGE,
      status: 'error',
    }))
  }
}
